"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "How is this different from the online homework platforms we already use?",
    answer: "Most platforms mark the final answer and tell you what a student got wrong. Setura reads the working and tells you why: whether the mark was lost to missing content knowledge or to a slip in method. The two need different fixes, and a score alone can't tell them apart."
  }, {
    question: "How accurate is the diagnosis, and can I overrule it?",
    answer: "Every diagnosis is tied to a specific line of working and a specific mark-scheme criterion, so you can check the reasoning, not just the verdict. Teachers can confirm or change any diagnosis, and those corrections make Setura more accurate. We're validating accuracy with our first pilot schools and will share the results."
  }, {
    question: "Does it add to my workload?",
    answer: "Students can type their working into Setura step by step, or you can upload their completed exam papers as they are, with no need to transcribe anything. Setura reads the working, matches it to the mark scheme and gives you the diagnosis for each student and the patterns across the class. The aim is to replace the time you spend working out why a class is losing marks, not to add another dashboard to check."
  }, {
    question: "Does Setura tell students the answer?",
    answer: "No. Setura diagnoses, it doesn't tutor. On a knowledge gap, it points the student to material their teacher has chosen. On a process error, it flags that something has gone wrong without saying what, so the student learns to find and fix their own mistakes."
  }, {
    question: "Which subjects and exam boards does it cover?",
    answer: "We're starting with GCSE maths for AQA, Edexcel and OCR, where mark schemes are the most structured. Setura is built to read any criteria-based mark scheme, and other subjects will follow."
  }, {
    question: "What happens to student data?",
    answer: "Student data is processed under UK GDPR, stored on UK servers, and covered by a data processing agreement with each school. Schools own their data and can ask us to delete it at any time. We use anonymised data to improve the accuracy of Setura's diagnosis, and never sell data or share it with third parties."
  }, {
    question: "What does a pilot involve?",
    answer: "One class for one half-term. We set it up with you and show you what Setura finds in your students' working. If it's useful, we extend to more classes and the rest of the course, and build it into how your department works long term."
  }];
  return <section data-section-id="724" id="pilot-faq" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="text-sm font-medium tracking-[0.2em] uppercase font-mono text-primary">
                FAQ
              </p>

              <h2 className="mt-4 text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] font-default font-medium tracking-tight">
                Diagnostic
                <br />
                Inquiries
              </h2>

              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-md">
                Need clarity on how Setura maps student performance? Our team is ready to discuss the technical implementation and diagnostic potential for your school.
              </p>

              <Link to="/PilotPage" className="mt-6 inline-flex items-center gap-2 text-base font-medium text-foreground hover:text-primary transition-colors group">
                <span className="border-b border-foreground/30 group-hover:border-primary pb-0.5">
                  Book a pilot call
                </span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Link>

              <div className="mt-12 hidden lg:block" aria-hidden="true">
                <svg viewBox="0 0 120 140" className="w-28 h-32 text-primary" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M30 45 C28 22, 52 12, 72 18 C92 24, 96 50, 78 62 C66 70, 58 74, 58 92" style={{
                  strokeDasharray: 200,
                  strokeDashoffset: leftVisible ? 0 : 200,
                  transition: "stroke-dashoffset 1.4s ease-out 0.3s"
                }} />
                  <circle cx="58" cy="112" r="3.5" fill="currentColor" stroke="none" style={{
                  opacity: leftVisible ? 1 : 0,
                  transition: "opacity 0.4s ease-out 1.5s"
                }} />
                </svg>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-100 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group data-[state=open]:bg-accent/60 transition-colors duration-300 rounded-md px-4 md:px-6 -mx-4 md:-mx-6">
                  <AccordionTrigger className="hover:no-underline py-6 md:py-7 [&>svg]:hidden gap-6">
                    <span className="text-xl md:text-2xl font-default font-medium text-left leading-snug text-foreground">
                      {faq.question}
                    </span>
                    <span className="relative flex-shrink-0 inline-flex items-center justify-center size-9 rounded-md transition-transform duration-300 group-data-[state=open]:rotate-45 bg-primary text-primary-foreground" aria-hidden="true">
                      <Plus className="size-5" strokeWidth={2.25} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 md:pb-7 pr-12 md:pr-16">
                    <p className="text-base md:text-[1.05rem] leading-relaxed text-muted-foreground">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}